import catalogLogo from "../../assets/logo.svg";
import {
  CATALOG_COLUMN_COUNT,
  groupsToColumnsByGridIndex,
  splitIntoColumns,
} from "../utils/catalogColumns";
import { resolveGroupThemeKey } from "../utils/catalogThemes";

/** A4 at 96dpi — fixed so layout never depends on monitor size. */
const PAGE_WIDTH_PX = 794;
const PAGE_HEIGHT_PX = 1123;
const CONTENT_WIDTH_PX = 766;

const GROUP_THEME_PALETTE = [
  { key: "sky", headerBg: "#edf5ff", headerBorder: "#cfe1ff", headerText: "#2563eb" },
  { key: "mint", headerBg: "#ecfbf3", headerBorder: "#c9f0dd", headerText: "#059669" },
  { key: "amber", headerBg: "#fff7ea", headerBorder: "#f7dfb1", headerText: "#d97706" },
  { key: "rose", headerBg: "#fff0f4", headerBorder: "#f7c8d6", headerText: "#e11d48" },
  { key: "violet", headerBg: "#f4efff", headerBorder: "#d9cdfd", headerText: "#7c3aed" },
  { key: "teal", headerBg: "#ecfbfc", headerBorder: "#bce9eb", headerText: "#0f766e" },
];

const getGroupTheme = (group) => {
  const themeKey = resolveGroupThemeKey(group);
  return (
    GROUP_THEME_PALETTE.find((theme) => theme.key === themeKey) || GROUP_THEME_PALETTE[0]
  );
};

/**
 * PDF board = same string grid index rules as UI:
 *   "11" → col1, "12" → col2, "62" → col2 (row sort), …
 * Break inside a group is allowed (group may continue on next page; header repeats).
 */
const PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 8mm 4mm;
  }
  * { box-sizing: border-box; }
  html, body {
    margin: 0 auto;
    padding: 0;
    width: ${PAGE_WIDTH_PX}px;
    min-width: ${PAGE_WIDTH_PX}px;
    max-width: ${PAGE_WIDTH_PX}px;
    height: auto;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 12px;
    background: #fff;
    color: #57606a;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .pricing-catalog-print-root {
    width: ${CONTENT_WIDTH_PX}px;
    max-width: ${CONTENT_WIDTH_PX}px;
    margin: 0 auto;
    padding: 0;
  }
  .print-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 3mm;
    margin-bottom: 3mm;
    padding: 0 1mm;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .print-header .left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2mm;
  }
  .print-title {
    font-size: 14px;
    font-weight: 600;
    color: #424a53;
  }
  .print-contacts {
    display: flex;
    flex-direction: column;
    gap: 1.5mm;
    align-items: flex-start;
  }
  .print-contact {
    position: relative;
    padding-left: 3mm;
    font-size: 12px;
    color: #000;
  }
  .print-contact::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1mm;
    height: 4mm;
    background: #2f80ed;
    border-radius: 0.5mm;
  }
  .print-meta {
    margin-top: 1.5mm;
    font-size: 11px;
    color: #6e7781;
    text-align: right;
  }
  /* 3 columns placed by group.index (same as UI board). */
  .catalog-print-board {
    display: table;
    table-layout: fixed;
    width: 100%;
    border-collapse: separate;
    border-spacing: 2.5mm 0;
  }
  .catalog-print-column {
    display: table-cell;
    vertical-align: top;
    width: 33.333%;
  }
  .group-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    margin: 0 0 2.5mm;
    border: 1px solid #dce3eb;
    border-radius: 2mm;
    background: #fff;
    /* Group MAY continue on the next page. */
    break-inside: auto;
    page-break-inside: auto;
    break-before: auto;
    break-after: auto;
    page-break-before: auto;
    page-break-after: auto;
  }
  .group-table thead {
    display: table-header-group;
  }
  .catalog-print-group-header {
    padding: 2.5mm 2mm;
    font-weight: 700;
    font-size: 13px;
    line-height: 1.2;
    border-bottom: 1px solid #dce3eb;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }
  .group-table tr.product-row {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .group-table td {
    padding: 1mm;
    border-bottom: 1px solid #e9eef2;
    font-size: 12px;
    color: #57606a;
    vertical-align: middle;
  }
  .group-table tr.product-row:last-child td {
    border-bottom: 0;
  }
  .group-table td.product-name {
    width: 46%;
    word-break: break-word;
    overflow-wrap: anywhere;
    line-height: 1.25;
  }
  .group-table td.variant {
    width: 34%;
    min-width: 24mm;
    white-space: nowrap;
    text-align: right;
    font-size: 11px;
    color: #6e7781;
    overflow: visible;
    text-overflow: clip;
    line-height: 1.25;
  }
  .group-table td.price {
    width: 20%;
    font-weight: 700;
    text-align: right;
    white-space: nowrap;
  }
  .group-table-empty td {
    text-align: center;
    color: #8b95a3;
    font-size: 13px;
    padding: 4mm 3mm;
  }
  .catalog-print-empty {
    text-align: center;
    color: #8b95a3;
    padding: 20mm 0;
    font-size: 14px;
  }
  @media print {
    html, body {
      width: ${PAGE_WIDTH_PX}px;
      min-width: ${PAGE_WIDTH_PX}px;
      max-width: ${PAGE_WIDTH_PX}px;
      height: auto;
    }
    .pricing-catalog-print-root {
      width: ${CONTENT_WIDTH_PX}px;
      max-width: ${CONTENT_WIDTH_PX}px;
    }
    .catalog-print-board {
      display: table;
      table-layout: fixed;
      width: 100%;
    }
    .catalog-print-column {
      display: table-cell;
      vertical-align: top;
    }
    .group-table {
      break-inside: auto !important;
      page-break-inside: auto !important;
      border-radius: 0;
    }
    .group-table thead {
      display: table-header-group;
    }
  }
`;

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const fmtPrice = (symbol, value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  const sym = escapeHtml(symbol || "€");
  return `${sym}${Math.round(num)}`;
};

const resolveLogoUrl = () => {
  if (!catalogLogo) return "";
  if (String(catalogLogo).startsWith("http")) return catalogLogo;
  return `${window.location.origin}${catalogLogo}`;
};

const sortProductsByIndex = (products = []) =>
  [...products].sort(
    (a, b) =>
      Number(a?.index ?? 0) - Number(b?.index ?? 0) ||
      Number(a?.id ?? 0) - Number(b?.id ?? 0)
  );

/** Collect groups from board columns or a flat list. */
const collectGroups = (groupColumns = []) => {
  if (!Array.isArray(groupColumns) || !groupColumns.length) return [];

  const looksLikeColumns = groupColumns.every((col) => Array.isArray(col));
  if (looksLikeColumns) {
    return groupColumns.flat().filter(Boolean);
  }

  return groupColumns.filter(Boolean);
};

/**
 * Place groups for PDF using string grid index (same helper as UI).
 * Ensures "62" lands in column 2 after lower-row indexes, etc.
 */
const buildPrintColumns = (groupColumns = []) => {
  const groups = collectGroups(groupColumns).map((group) => ({
    ...group,
    index: group.index != null ? String(group.index).trim() : group.index,
    products: sortProductsByIndex(group.products || []),
  }));

  if (!groups.length) {
    return Array.from({ length: CATALOG_COLUMN_COUNT }, () => []);
  }

  const byIndex = groupsToColumnsByGridIndex(groups, CATALOG_COLUMN_COUNT);
  if (byIndex.some((col) => col.length)) {
    return byIndex;
  }

  // Fallback only if every index failed to parse
  return splitIntoColumns(groups, CATALOG_COLUMN_COUNT).map((column) =>
    column.map(({ item }) => item)
  );
};

const countGroups = (columnStacks = []) =>
  columnStacks.reduce((sum, col) => sum + (col || []).filter(Boolean).length, 0);

const renderGroup = (group) => {
  const theme = getGroupTheme(group);
  const products = sortProductsByIndex(group.products || []);
  const rows = products.length
    ? products
        .map(
          (product) => `
        <tr class="product-row">
          <td class="product-name">${escapeHtml(product.name || "")}</td>
          <td class="variant">${escapeHtml(product.variant || "")}</td>
          <td class="price">${fmtPrice(group.currencySymbol, product.price)}</td>
        </tr>`
        )
        .join("")
    : `<tr class="group-table-empty"><td colspan="3">No products in this group</td></tr>`;

  return `
      <table class="group-table">
        <colgroup>
          <col style="width:46%" />
          <col style="width:34%" />
          <col style="width:20%" />
        </colgroup>
        <thead>
          <tr>
            <th
              colspan="3"
              class="catalog-print-group-header"
              style="background:${theme.headerBg};border-color:${theme.headerBorder};color:${theme.headerText};"
            >
              ${escapeHtml(group.name || "Group")}
            </th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`;
};

const renderPrintHeader = (logoSvg, printDate) => `
        <div class="print-header">
          <div class="left">
            ${logoSvg}
            <div class="print-title">Mobile Pricing Catalog</div>
          </div>
          <div>
            <div class="print-contacts">
              <div class="print-contact">+49 1577 777 3887 (WhatsApp available)</div>
              <div class="print-contact">Lärchenstraße 78, 65933 Frankfurt am Main</div>
              <div class="print-contact">info@mrphone.gmbh</div>
            </div>
            <div class="print-meta">${escapeHtml(printDate)}</div>
          </div>
        </div>`;

const renderBoardColumns = (columnStacks = []) =>
  columnStacks
    .map(
      (col) => `
      <div class="catalog-print-column">
        ${(col || []).filter(Boolean).map((group) => renderGroup(group)).join("\n")}
      </div>`
    )
    .join("\n");

/** Renders isolated print HTML — columns from group string index (same as UI). */
export function renderPrintHtml(groupColumns = []) {
  const columnStacks = buildPrintColumns(groupColumns);
  const groupCount = countGroups(columnStacks);
  const logoUrl = resolveLogoUrl();
  const logoSvg = logoUrl
    ? `<img src="${escapeHtml(logoUrl)}" alt="Mr.Phone" style="height: 9mm; width: auto; max-width: 52mm; object-fit: contain;" />`
    : "";

  const printDate = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const bodyHtml = !groupCount
    ? `<p class="catalog-print-empty">No product groups to export.</p>`
    : `${renderPrintHeader(logoSvg, printDate)}
      <div class="catalog-print-board">
        ${renderBoardColumns(columnStacks)}
      </div>`;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=${PAGE_WIDTH_PX}, initial-scale=1, maximum-scale=1, user-scalable=no" />
    <title>Print - Pricing Catalog</title>
    <style>${PRINT_STYLES}</style>
  </head>
  <body>
    <div class="pricing-catalog-print-root">
      ${bodyHtml}
    </div>
  </body>
</html>`;
}

const lockPrintDocumentLayout = (doc, iframe) => {
  const pageWidth = `${PAGE_WIDTH_PX}px`;
  const contentWidth = `${CONTENT_WIDTH_PX}px`;

  [doc.documentElement, doc.body].forEach((el) => {
    if (!el) return;
    el.style.width = pageWidth;
    el.style.minWidth = pageWidth;
    el.style.maxWidth = pageWidth;
    el.style.margin = "0 auto";
    el.style.height = "auto";
  });

  const root = doc.querySelector(".pricing-catalog-print-root");
  if (root) {
    root.style.width = contentWidth;
    root.style.maxWidth = contentWidth;
  }

  const board = doc.querySelector(".catalog-print-board");
  if (board) {
    board.style.display = "table";
    board.style.tableLayout = "fixed";
    board.style.width = "100%";
    board.style.borderCollapse = "separate";
    board.style.borderSpacing = "2.5mm 0";
  }

  if (iframe) {
    const contentHeight = Math.max(
      PAGE_HEIGHT_PX,
      doc.documentElement?.scrollHeight || 0,
      doc.body?.scrollHeight || 0
    );
    iframe.style.height = `${contentHeight}px`;
  }
};

/** Print via hidden iframe at fixed A4 width — height grows with content. */
export function printPricingCatalog(groupColumns = []) {
  return new Promise((resolve, reject) => {
    try {
      const html = renderPrintHtml(groupColumns);
      const iframe = document.createElement("iframe");
      iframe.setAttribute("title", "Pricing catalog print");
      iframe.setAttribute(
        "style",
        [
          "position:fixed",
          "left:-10000px",
          "top:0",
          `width:${PAGE_WIDTH_PX}px`,
          `min-width:${PAGE_WIDTH_PX}px`,
          `min-height:${PAGE_HEIGHT_PX}px`,
          "height:auto",
          "border:0",
          "visibility:hidden",
        ].join(";")
      );
      document.body.appendChild(iframe);

      const win = iframe.contentWindow;
      if (!win) {
        document.body.removeChild(iframe);
        reject(new Error("Unable to create print frame"));
        return;
      }

      let finished = false;
      let printStarted = false;

      const cleanup = () => {
        if (finished) return;
        finished = true;
        setTimeout(() => {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
          resolve();
        }, 300);
      };

      const triggerPrint = () => {
        if (printStarted) return;
        printStarted = true;
        try {
          lockPrintDocumentLayout(win.document, iframe);
          win.focus();
          win.print();
        } catch (err) {
          cleanup();
          reject(err);
        }
      };

      win.onafterprint = cleanup;

      const doc = win.document;
      doc.open();
      doc.write(html);
      doc.close();

      const waitForAssets = () => {
        if (printStarted || finished) return;
        lockPrintDocumentLayout(doc, iframe);

        const logo = doc.querySelector("img");
        const tasks = [];

        if (doc.fonts?.ready) {
          tasks.push(doc.fonts.ready.catch(() => {}));
        }

        if (logo && !logo.complete) {
          tasks.push(
            new Promise((res) => {
              logo.addEventListener("load", res, { once: true });
              logo.addEventListener("error", res, { once: true });
            })
          );
        }

        const startPrint = () => {
          lockPrintDocumentLayout(doc, iframe);
          setTimeout(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(triggerPrint);
            });
          }, 200);
        };

        if (tasks.length) {
          Promise.all(tasks).then(startPrint);
        } else {
          startPrint();
        }
      };

      if (doc.readyState === "complete") {
        waitForAssets();
      } else {
        iframe.onload = waitForAssets;
      }

      setTimeout(() => {
        if (!printStarted && !finished) {
          finished = true;
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
          reject(new Error("Print did not start"));
        }
      }, 15000);

      setTimeout(() => {
        if (printStarted && !finished) cleanup();
      }, 120000);
    } catch (err) {
      reject(err);
    }
  });
}

export default function PrintView() {
  return null;
}
